'use client';

import * as React from 'react';
import { ChevronsUpDown } from 'lucide-react';

import { DialogClose } from '@/components/atoms/primitives/dialog';
import { cn } from '@/components/atoms/primitives/utils';
import { GN_FOCUS_RING } from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';

/**
 * MenuWayfinderCard — the single carded Wayfinder entry shared by city + app
 * menu bodies (WEB-639). Layer rows live in the header-mounted WayfinderSheet;
 * activating this card dismisses the menu and opens that sheet.
 */

export interface MenuWayfinderCardProps {
  /** Host label shown after "You are here ·" (e.g. `clt.loci.city`). */
  readonly hostLabel: string;
  /** Secondary line under the host label. */
  readonly subtitle: string;
  /** Accessible name for the card control. */
  readonly ariaLabel: string;
  /** `data-testid` for the interactive card. */
  readonly testId: string;
  /**
   * Called synchronously before the deferred header-Wayfinder open, so the
   * host can suppress menu focus-return (mirrors Search Charlotte).
   */
  readonly onBeforeOpen?: () => void;
  readonly className?: string;
}

/**
 * Open the header-mounted Wayfinder trigger after the menu Dialog has begun
 * dismissing, so the modal layer does not swallow the popover. Shared with the
 * city account drawer's "Switch city" row (FRE-1389).
 */
export function activateHeaderWayfinder(): void {
  window.requestAnimationFrame(() => {
    document.querySelector<HTMLButtonElement>('[data-testid="wayfinder-trigger"]')?.click();
  });
}

export function MenuWayfinderCard({
  hostLabel,
  subtitle,
  ariaLabel,
  testId,
  onBeforeOpen,
  className,
}: MenuWayfinderCardProps): React.ReactElement {
  const handleClick = React.useCallback(() => {
    onBeforeOpen?.();
    activateHeaderWayfinder();
  }, [onBeforeOpen]);

  return (
    <DialogClose asChild>
      <button
        type="button"
        aria-label={ariaLabel}
        data-testid={testId}
        onClick={handleClick}
        className={cn(
          'mx-2 grid min-h-11 w-[calc(100%-1rem)] grid-cols-[28px_1fr_auto] items-center gap-3 rounded-[14px] border border-[var(--gn-accent)] bg-[var(--gn-accent-wash)] px-3 py-2.5 text-left md:mx-3 md:w-[calc(100%-1.5rem)]',
          'transition-colors duration-150 hover:bg-[var(--gn-accent-wash)] motion-reduce:transition-none',
          GN_FOCUS_RING,
          className,
        )}
      >
        <span
          aria-hidden="true"
          className="grid size-7 place-items-center rounded-ds-md bg-[var(--gn-accent)] font-mono text-micro font-semibold text-clt-navy"
        >
          ◐
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate font-mono text-caption tracking-[0.02em] text-fg-1">
            You are here · {hostLabel}
          </span>
          <span className="truncate text-micro text-[#9AA3B5]">{subtitle}</span>
        </span>
        <ChevronsUpDown
          aria-hidden="true"
          className="size-3.5 shrink-0 text-[var(--gn-accent-glow)]"
          strokeWidth={2}
        />
      </button>
    </DialogClose>
  );
}
